import { getCompanyHasFeatureAccess } from "@repo/app-types/helpers/billingFeatureGating";
import { SaveAndCancelButtons } from "@repo/ui/Buttons/saveAndCancelButtons";
import { Textarea } from "@repo/ui/Inputs/textarea";
import { Link } from "@repo/ui/Link/index";
import { IconLock } from "@tabler/icons-react";
import { type FC, useState } from "react";
import { appPaths } from "../../../app/appPaths.js";
import { useAppSelector } from "../../../hooks/hook.js";
import { UpgradeText } from "../../billing/upgradeText.js";
import { selectCompany } from "../../company/companySlice.js";

const MIN_CONTEXT_LENGTH = 15;
const MAX_CONTEXT_LENGTH = 3000;

interface AIContextSettingProps {
  context: string | null;
  onSave: (newContext: string | null) => void;
}

export const AIContextSetting: FC<AIContextSettingProps> = (props) => {
  const { onSave } = props;

  const company = useAppSelector(selectCompany);
  const isMissingFeatureAccess = Boolean(
    company && !getCompanyHasFeatureAccess(company, "project_level_context"),
  );

  const context = props.context ?? "";
  const [newContext, setNewContext] = useState(context);
  const [contextError, setContextError] = useState("");

  const upgradeSpan = isMissingFeatureAccess ? (
    <UpgradeText planFeatureName="project_level_context" />
  ) : null;

  const onClickSaveContext = (): void => {
    if (newContext && newContext.length < MIN_CONTEXT_LENGTH) {
      setContextError(
        `Must be at least ${MIN_CONTEXT_LENGTH.toString()} characters long`,
      );
      return;
    }

    if (newContext && newContext.length > MAX_CONTEXT_LENGTH) {
      setContextError(
        `Must be less than ${MAX_CONTEXT_LENGTH.toString()} characters long`,
      );
      return;
    }

    setContextError("");
    onSave(newContext);
  };

  return (
    <div className="flex flex-col space-y-3">
      <Textarea
        label="Custom AI context"
        value={newContext}
        onChange={(newValue) => {
          setContextError("");
          setNewContext(newValue);
        }}
        maxLength={MAX_CONTEXT_LENGTH}
        placeholder="Add custom context about this project..."
        errorDescription={contextError}
        description={
          <>
            Custom context for this project that should be used to generate more
            relevant AI clarifying questions (max {MAX_CONTEXT_LENGTH}{" "}
            characters). When empty, your{" "}
            <Link href={appPaths.settingsOrganization.build()} sameTab>
              product or service description
            </Link>{" "}
            will be used.{" "}
            <Link href="https://help.alpharun.com/en/articles/2105857">
              Check out the guide
            </Link>{" "}
            for examples.
            {upgradeSpan}
          </>
        }
        leftIcon={
          isMissingFeatureAccess ? (
            <IconLock className="h-4 w-4 text-gray-700" />
          ) : undefined
        }
        isDisabled={isMissingFeatureAccess}
        className="w-[700px]"
      />
      {context !== newContext ? (
        <SaveAndCancelButtons
          onSave={onClickSaveContext}
          onCancel={() => {
            setNewContext(context || "");
            setContextError("");
          }}
        />
      ) : null}
    </div>
  );
};
